import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./config/firebase";
import { getAuthToken } from "./utils/getAuthToken";
import { initializeUnitCoErrorTracking } from "./utils/unitcoErrorTracker";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
//import { loadZendesk, unloadZendesk } from './utils/loadZendesk';
import "./types/custom-elements.d.ts";

// Analytics
import { initializeAnalytics, usePageTracking } from "./services/analytics";
// Observability (HyperDX)
import { initializeHyperDX, setHyperDXUser, clearHyperDXUser } from "./services/observability";
import { HyperDXErrorBoundary } from "./components/ErrorBoundary";
import { CookieConsentBanner } from "./components/CookieConsent";
// Layout Components
import MainLayout from "./components/Layout/MainLayout";
import MobileLandscapeBlocker from "./components/Layout/MobileLandscapeBlocker";
import Navbar from "./components/Layout/Navbar";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
// Pages
import LoginPage from "./pages/Login";
import AppleDomainVerification from "./components/AppleDomainVerification";
import SupportPage from "./pages/SupportPage";
import CustomerDetailsPage from "./pages/admin/CustomerDetailsPage";
import CustomerProfilePage from "./pages/CustomerProfilePage";
import UsersListPage from "./pages/admin/UsersListPage";
import UserProfilePage from "./pages/admin/UserProfilePage";
import SettingsPage from "./pages/SettingsPage";
import BankingPage from "./pages/BankingPage";
import BankingActivitiesPage from "./pages/BankingActivitiesPage";
import DistributionPage from "./pages/DistributionPage";
import DashboardPage from "./pages/DashboardPage";
import NebulaPage from "./pages/NebulaPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SignupPage from "./pages/SignupPage";
import { AuthProvider, useAuth } from "./components/Auth/AuthContext";
import { UserProvider, useUser } from "./contexts/UserContext";
import { ArtistProvider } from "./contexts/ArtistContext";
import ProfilePage from "./pages/ProfilePage";
import MerchandisePage from "./pages/MerchandisePage";
import BeatShopBrowsePage from "./store/beatshop/pages/BrowsePage";
import BeatShopDetailPage from "./store/beatshop/pages/BeatDetailPage";
import BeatShopVaultPage from "./store/beatshop/pages/VaultPage";
import BeatShopVaultDeliveryPage from "./store/beatshop/pages/VaultDeliveryPage";
import BeatShopSellerPage from "./store/beatshop/pages/SellerPage";
import BeatShopAdminPage from "./store/beatshop/pages/AdminPage";
import BeatShopLayout from "./store/beatshop/BeatShopLayout";
import LendingPage from "pages/LendingPage";
import ConfirmAccountDetails from "./pages/ConfirmAccountDetails";
import { SessionExpiredProvider, useSessionExpired } from "./contexts/SessionExpiredContext";
import { SurveyProvider } from "./contexts/SurveyContext";
import SurveyPopup from "./components/Survey/SurveyPopup";
import { AnnouncementProvider } from "./contexts/AnnouncementContext";
import AnnouncementPopup from "./components/Announcement/AnnouncementPopup";
import { setSessionExpiredModalCallback } from "./utils/logoutHandler";
import ArtistMappingPage from "./pages/admin/ArtistMappingPage";
import TeamsListPage from "./pages/admin/TeamsListPage";
import TeamDetailsPage from "./pages/admin/TeamDetailsPage";
import CreateTeamPage from "./pages/admin/CreateTeamPage";
import InvitationsListPage from "./pages/admin/InvitationsListPage";
import RolesListPage from "./pages/admin/RolesListPage";
import NebulaInsightsPage from "./pages/admin/NebulaInsightsPage";
import SurveysPage from "./pages/admin/SurveysPage";
import AnnouncementsPage from "./pages/admin/AnnouncementsPage";
import AccountRequestsPage from "./pages/admin/AccountRequestsPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import { NEBULA_INSIGHTS_ENABLED } from "./config/featureFlags";
import PermissionsListPage from "./pages/admin/PermissionsListPage";
import AccountTransactionsPage from "pages/admin/AccountTransactionsPage";
import AccountTransactionsMockPage from "pages/admin/AccountTransactionsMockPage";
import AccountTransactionsSummaryPage from "pages/admin/AccountTransactionsSummaryPage";
import InvitationsPage from "./pages/InvitationsPage";
import AcceptInvitationPageWrapper from "./pages/AcceptInvitationPage";
import PermissionProtectedRoute from "./components/Auth/PermissionProtectedRoute";
import NotFoundPage from "./pages/NotFoundPage";
import PayoutDashboardPage from "./pages/PayoutDashboardPage";
import PayoutMethodSelectionPage from "./pages/PayoutMethodSelectionPage";
import PayoutExcelUploadPage from "./pages/PayoutExcelUploadPage";
import PayoutPreviewPage from "./pages/PayoutPreviewPage";
import PayoutAccountSelectionPage from "./pages/PayoutAccountSelectionPage";
import PayoutAmountEntryPage from "./pages/PayoutAmountEntryPage";
import PayoutDuplicateWarningPage from "./pages/PayoutDuplicateWarningPage";
import PayoutConfirmationPage from "./pages/PayoutConfirmationPage";
import PayoutQueueSubmittedPage from "./pages/PayoutQueueSubmittedPage";
import PayoutQueueStatusPage from "./pages/PayoutQueueStatusPage";
import PayoutLedgerPage from "./pages/PayoutLedgerPage";
import ProfileLayoutPage from "./pages/profile/ProfileLayoutPage";
import ProfileAccountInformationPage from "./pages/profile/ProfileAccountInformationPage";
import ProfileChangePasswordPage from "./pages/profile/ProfileChangePasswordPage";
import ProfileArtistInformationPage from "./pages/profile/ProfileArtistInformationPage";

//const ZENDESK_WIDGET_KEY = process.env.REACT_APP_ZENDESK_WIDGET_KEY!; // Replace with your real key
// Protected Route Component - no token validation here to avoid excessive calls
// Token validation happens on actual route changes in AppContent, not on every render
const ProtectedRoute = ({ isAuthenticated, children }: { isAuthenticated: boolean; children: React.ReactNode }) => {
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const AppWrapper = () => {
  return (
    <HyperDXErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <UserProvider>
            <ArtistProvider>
              <SessionExpiredProvider>
                <SidebarProvider>
                  <AppContent />
                </SidebarProvider>
              </SessionExpiredProvider>
            </ArtistProvider>
          </UserProvider>
        </AuthProvider>
      </BrowserRouter>
    </HyperDXErrorBoundary>
  );
};

const DashboardWithProfileCheck = () => {
  const { loading, shouldRedirectToConfirm } = useUser();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  
  if (shouldRedirectToConfirm) {
    return <Navigate to="/confirm-account-details" replace />;
  }

  return (
    <PermissionProtectedRoute>
      <DashboardPage />
    </PermissionProtectedRoute>
  );
};

const ProtectedRouteWithProfileCheck = ({ children }: { children: React.ReactNode }) => {
  const { loading, shouldRedirectToConfirm } = useUser();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  
  if (shouldRedirectToConfirm) {
    return <Navigate to="/confirm-account-details" replace />;
  }

  return (
    <PermissionProtectedRoute>
      {children}
    </PermissionProtectedRoute>
  );
};

const AppContent = () => {
  //const [isAuthenticated, setIsAuthenticated] = useState(false);
  //const [loading, setLoading] = useState(true);
  const { isAuthenticated, loading } = useAuth();
  const { toggleSidebar } = useSidebar();
  const { showSessionExpired } = useSessionExpired();

  const location = useLocation();

  // Initialize analytics page tracking
  usePageTracking();

  // Initialize session expired modal callback
  useEffect(() => {
    setSessionExpiredModalCallback(showSessionExpired);
  }, [showSessionExpired]);

  // Initialize analytics and HyperDX once when app starts
  useEffect(() => {
    initializeAnalytics();
    initializeHyperDX();
  }, []);

  // Initialize UnitCo error tracking once when app starts
  useEffect(() => {
    initializeUnitCoErrorTracking();
    
    // Listen for UnitCo API errors
    const handleUnitCoApiError = (e: CustomEvent) => {
      const errorDetail = e.detail;
      console.error('🔔 UnitCo API Error Event:', errorDetail);
      
      // You can add additional error handling here, like sending to analytics
      // or showing a toast notification
    };
    
    window.addEventListener('unitcoApiError', handleUnitCoApiError as EventListener);
    
    return () => {
      window.removeEventListener('unitcoApiError', handleUnitCoApiError as EventListener);
    };
  }, []);

  // Event-based token validation only - no intervals to avoid Firebase rate limits
  // Validation happens on: actual route changes (not clicks), visibility changes, form submissions

  // Track last validated route to prevent re-validation on same route
  const lastValidatedRouteRef = useRef<string>('');

  // Validate token on route changes (only for protected routes, only when route actually changes)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const protectedPaths = ['/dashboard', '/profile', '/settings', '/nebula', '/lending', 
      '/merchandise', '/distribution', '/coming-soon', '/customer-service', '/customer-details', '/account-transactions-summary', '/users-list', '/user-profile', '/banking', '/teams', '/invitations', '/admin/roles', '/admin/surveys', '/admin/announcements', '/admin/account-requests', '/permissions', '/invitations/accept',
      ...(NEBULA_INSIGHTS_ENABLED ? ['/admin/nebula-insights'] : []),
    ];
    
    // Only validate if we're on a protected route AND route actually changed
    const isProtectedRoute = protectedPaths.some(path => location.pathname.startsWith(path));
    const routeChanged = location.pathname !== lastValidatedRouteRef.current;
    
    if (!isProtectedRoute || !routeChanged) {
      return; // Don't validate if not a protected route or if route didn't change
    }
    
    // Update last validated route
    lastValidatedRouteRef.current = location.pathname;
    
    // Throttle route validation - small delay to batch rapid route changes
    const timeoutId = setTimeout(async () => {
      try {
        // Force refresh on route change to immediately detect password changes
        await getAuthToken(true);
      } catch (error: any) {
        // Token validation failed - error handling in getAuthToken will trigger logout
        console.error("[AppContent] Token validation failed on route change:", error);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, isAuthenticated]);

  // Validate token only when tab becomes visible (user switches back from another tab/app)
  // Removed window focus handler - it was triggering on every click
  useEffect(() => {
    if (!isAuthenticated) return;

    let lastVisibilityCheck = 0;
    const VISIBILITY_CHECK_COOLDOWN = 5 * 60 * 1000; // 5 minutes - don't check too frequently

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // Throttle: Only check if last check was more than 5 minutes ago
        if (now - lastVisibilityCheck < VISIBILITY_CHECK_COOLDOWN) {
          return;
        }
        lastVisibilityCheck = now;
        
        try {
          // Validate token when user returns to the tab after being away
          await getAuthToken(true); // Force refresh to check validity
        } catch (error: any) {
          // Token validation failed - error handling in getAuthToken will trigger logout
          console.error("[AppContent] Token validation failed on visibility change:", error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);
  // Initialize Zendesk only once on mount
  // useEffect(() => {
  //   const unsubscribe = onAuthStateChanged(auth, (user) => {
  //     //Check isAuthenticated from localStorage
  //     // Delay to ensure login logic is finished
  //     setTimeout(() => {
  //       const loggedIn = !!user && localStorage.getItem("isAuthenticated") === "true";
  //       console.log("isAuthenticated: ", loggedIn);
  //       setIsAuthenticated(loggedIn);
  //       setLoading(false);
  //     }, 200); // ⏳ small delay (~200ms)
  //   });

  //   return () => unsubscribe();
  // }, []);

  // Show/hide widget based on auth + current route
  // useEffect(() => {
  //   const waitForZendesk = () => {
  //     if (!window.zE) {
  //       setTimeout(waitForZendesk, 200);
  //       return;
  //     }

  //     const isCustomerServicePage = location.pathname === '/support';
  //     console.log("isCustomerServicePage: ", isCustomerServicePage);
  //     console.log("isAuthenticated: ", isAuthenticated);  
  //     console.log("location.pathname: ", location.pathname);
  //     if (isAuthenticated && isCustomerServicePage) {
  //       window.zE('messenger', 'show');
  //       window.zE('messenger', 'open');
  //     } else {
  //       window.zE('messenger', 'hide');
  //     }
  //   };

  //   waitForZendesk();
  // }, [isAuthenticated, location.pathname]);
  
  const getZoomLevel = () => window.devicePixelRatio || 1;
  
  // function waitForZendesk(callback: () => void, maxTries = 10) {
  //   const interval = setInterval(() => {
  //     if (window.zE) {
  //       clearInterval(interval);
  //       callback();
  //     } else if (--maxTries <= 0) {
  //       clearInterval(interval);
  //     }
  //   }, 500);
  // }

  useEffect(() => {
//     console.log("firebase projectId:", auth.app.options.projectId);
// console.log("firebase authDomain:", auth.app.options.authDomain);
// console.log("firebase apiKey:", auth.app.options.apiKey);
// console.log("browser origin:", window.location.origin);

    let zoom = getZoomLevel();
    const aside = document.querySelector('aside');
    //console.log('Zoom level:', zoom); // 1 = 100%, 1.25 = 125%, etc.
    if (window.innerWidth >= 768){
       // Always force zoom to 100% by inverting actual zoom
      document.body.style.setProperty('zoom', '1');
      // if (zoom > 1 || zoom < 1) {
      //     if(zoom < 1){
      //       zoom = 1;
      //     }
      //     // Apply zoom fix (scale content if zoomed in only)
      //     if (zoom > 1) {
      //       document.body.style.setProperty('zoom', `${1 / zoom}`);
      //     } else {
      //       document.body.style.removeProperty('zoom');
      //     }
      //     // document.body.style.transform = `scale(${1 / zoom})`;
      //     // document.body.style.transformOrigin = 'top left';
      //     // document.body.style.width = `${zoom * 100}%`;
      // }
      if (aside && zoom > 1) {
        const extraHeight = zoom * 100; // Increase height proportionally
        aside.style.minHeight = `${window.innerHeight + extraHeight}px`;
        //console.log("extraHeight: ", extraHeight, `${window.innerHeight + extraHeight}px`)
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      document.body.style.backgroundColor = "#F8F7F2";
    } else {
      document.body.style.backgroundColor = "#F8F7F2"; // or a different color
    }
  }, [isAuthenticated]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // ✅ Ensure useLocation() is inside BrowserRouter
  //const showNavbar = location.pathname !== "/login" && location.pathname !== "/signup";
  return (
    <AnnouncementProvider isAuthenticated={isAuthenticated}>
      <SurveyProvider isAuthenticated={isAuthenticated}>
        <Navbar isAuthenticated={isAuthenticated} onSidebarToggle={toggleSidebar} />
        <AnnouncementPopup />
        <SurveyPopup />
        <MobileLandscapeBlocker />
        <Routes>
        {/* Apple Developer Domain Verification Route - Must be public and accessible without authentication */}
        <Route path="/.well-known/apple-developer-domain-association.txt" element={<AppleDomainVerification />} />

        {/* Public Routes - Redirect if already authenticated */}
        <Route path="/" element={
          isAuthenticated ? 
            <Navigate to="/dashboard" replace /> : 
            <Navigate to="/login" replace />
        } />
        
        <Route path="/login" element={
          isAuthenticated ? 
            <Navigate to="/dashboard" replace /> : 
            <MainLayout showSidebar={false}><LoginPage /></MainLayout>
        } />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute isAuthenticated={isAuthenticated}><MainLayout showSidebar={true} /></ProtectedRoute>}>
          {/* Profile submenu (split forms) */}
          {/* <Route path="profile" element={<ProtectedRouteWithProfileCheck><ProfilePage /></ProtectedRouteWithProfileCheck>} /> */}
          <Route path="profile" element={<ProtectedRouteWithProfileCheck><ProfileLayoutPage /></ProtectedRouteWithProfileCheck>}>
            <Route index element={<Navigate to="/profile/account" replace />} />
            <Route path="account" element={<ProfileAccountInformationPage />} />
            <Route path="change-password" element={<ProfileChangePasswordPage />} />
            <Route path="artist" element={<ProfileArtistInformationPage />} />
          </Route>
          <Route path="settings" element={<ProtectedRouteWithProfileCheck><ProfilePage /></ProtectedRouteWithProfileCheck>} />
          <Route path="dashboard" element={<DashboardWithProfileCheck />} />
          <Route path="nebula" element={<ProtectedRouteWithProfileCheck><NebulaPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="lending" element={<ProtectedRouteWithProfileCheck><LendingPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="merchandise" element={<ProtectedRouteWithProfileCheck><MerchandisePage /></ProtectedRouteWithProfileCheck>} />
          <Route path="distribution" element={<ProtectedRouteWithProfileCheck><DistributionPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="store/beat-shop" element={<ProtectedRouteWithProfileCheck><BeatShopLayout /></ProtectedRouteWithProfileCheck>}>
            <Route index element={<BeatShopBrowsePage />} />
            <Route path="beats/:beatId" element={<BeatShopDetailPage />} />
            <Route path="vault" element={<BeatShopVaultPage />} />
            <Route path="vault/:entitlementId" element={<BeatShopVaultDeliveryPage />} />
            <Route path="sell" element={<BeatShopSellerPage />} />
          </Route>
          <Route path="admin/beat-shop" element={<ProtectedRouteWithProfileCheck><BeatShopAdminPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="coming-soon/:feature" element={<ProtectedRouteWithProfileCheck><ComingSoonPage /></ProtectedRouteWithProfileCheck>} />
          {/* <Route index element={<Navigate to="/settings" />} />           */}
          <Route path="customer-service" element={<ProtectedRouteWithProfileCheck><SupportPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="customer-details" element={<ProtectedRouteWithProfileCheck><CustomerDetailsPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="/account-transactions/:accountId" element={<AccountTransactionsPage />} />
          <Route path="/account-transactions-summary" element={<AccountTransactionsSummaryPage />} />
          <Route path="/account-transactions-mock" element={<AccountTransactionsMockPage />} />
          <Route path="users-list" element={<ProtectedRouteWithProfileCheck><UsersListPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="customer-profile/:customerId" element={<ProtectedRouteWithProfileCheck><CustomerProfilePage /></ProtectedRouteWithProfileCheck>} />
          <Route path="user-profile/:userId" element={<ProtectedRouteWithProfileCheck><UserProfilePage /></ProtectedRouteWithProfileCheck>} />
          <Route path="banking" element={<ProtectedRouteWithProfileCheck><BankingPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="banking/activities" element={<ProtectedRouteWithProfileCheck><BankingActivitiesPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="artist-mapping" element={<ProtectedRouteWithProfileCheck><ArtistMappingPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="teams" element={<ProtectedRouteWithProfileCheck><TeamsListPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="teams/create" element={<ProtectedRouteWithProfileCheck><CreateTeamPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="teams/:teamId" element={<ProtectedRouteWithProfileCheck><TeamDetailsPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="invitations/accept/:token" element={<ProtectedRouteWithProfileCheck><AcceptInvitationPageWrapper /></ProtectedRouteWithProfileCheck>} />
          <Route path="invitations" element={<ProtectedRouteWithProfileCheck><InvitationsPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="admin/invitations" element={<ProtectedRouteWithProfileCheck><InvitationsListPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="admin/roles" element={<ProtectedRouteWithProfileCheck><RolesListPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="admin/surveys" element={<ProtectedRouteWithProfileCheck><SurveysPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="admin/announcements" element={<ProtectedRouteWithProfileCheck><AnnouncementsPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="admin/account-requests" element={<ProtectedRouteWithProfileCheck><AccountRequestsPage /></ProtectedRouteWithProfileCheck>} />
          {/* Nebula Insights disabled – set NEBULA_INSIGHTS_ENABLED in config/featureFlags.ts */}
          {NEBULA_INSIGHTS_ENABLED ? (
            <Route path="admin/nebula-insights" element={<ProtectedRouteWithProfileCheck><NebulaInsightsPage /></ProtectedRouteWithProfileCheck>} />
          ) : (
            <Route path="admin/nebula-insights" element={<NotFoundPage />} />
          )}
          <Route path="permissions" element={<ProtectedRouteWithProfileCheck><PermissionsListPage /></ProtectedRouteWithProfileCheck>} />
          {/* Payouts routes disabled – show 404. Restore by replacing this route with the commented block below. */}
          <Route path="payouts/*" element={<NotFoundPage />} />
          {/* <Route path="payouts/dashboard" element={<ProtectedRouteWithProfileCheck><PayoutDashboardPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/new" element={<ProtectedRouteWithProfileCheck><PayoutMethodSelectionPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/upload" element={<ProtectedRouteWithProfileCheck><PayoutExcelUploadPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/preview" element={<ProtectedRouteWithProfileCheck><PayoutPreviewPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/select-accounts" element={<ProtectedRouteWithProfileCheck><PayoutAccountSelectionPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/amount-entry" element={<ProtectedRouteWithProfileCheck><PayoutAmountEntryPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/duplicate-warning" element={<ProtectedRouteWithProfileCheck><PayoutDuplicateWarningPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/confirmation" element={<ProtectedRouteWithProfileCheck><PayoutConfirmationPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/queue-submitted" element={<ProtectedRouteWithProfileCheck><PayoutQueueSubmittedPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/queue" element={<ProtectedRouteWithProfileCheck><PayoutQueueStatusPage /></ProtectedRouteWithProfileCheck>} />
          <Route path="payouts/ledger" element={<ProtectedRouteWithProfileCheck><PayoutLedgerPage /></ProtectedRouteWithProfileCheck>} /> */}
          {/* <Route path="dashboard" element={<div className="p-4">Dashboard Content</div>} /> */}
          {/* <Route path="/settings" element={<SettingsPage />} /> */}
          
        </Route>
        
        {/* Confirm Account Details - Authenticated but no profile completion check */}
        <Route path="confirm-account-details" element={
          isAuthenticated ? 
            <MainLayout showSidebar={false}><ConfirmAccountDetails /></MainLayout> : 
            <Navigate to="/login" replace />
        } />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="signup" element={<SignupPage />} />
        {/* Catch-all route - 404 Not Found for any unmatched routes */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          theme="colored"
          closeOnClick
          pauseOnHover
        />
        <CookieConsentBanner />
      </SurveyProvider>
    </AnnouncementProvider>
  );
};

export default AppWrapper;
